import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,  
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import SchoolIcon from "@mui/icons-material/School";
import CloudIcon from "@mui/icons-material/Cloud";
import VerifiedIcon from "@mui/icons-material/Verified";
import HandshakeIcon from "@mui/icons-material/Handshake";

const values = [
  {
    title: "Education-first thinking",
    icon: <SchoolIcon />,
    desc: "We build with learners, teachers, and parents in mind—simple flows, clear outcomes, and measurable impact.",
  },
  {
    title: "Cloud-native delivery",
    icon: <CloudIcon />,
    desc: "Secure, scalable architectures designed for real usage patterns and growth—without unnecessary complexity.",
  },
  {
    title: "Quality and reliability",
    icon: <VerifiedIcon />,
    desc: "Testing, reviews, and disciplined delivery practices to reduce regressions and keep products stable.",
  },
  {
    title: "Long-term partnership",
    icon: <HandshakeIcon />,
    desc: "We work as an extension of your team—transparent communication and pragmatic engineering decisions.",
  },
];

const AboutUs = () => {
  return (
    <Box component="main">
      {/* HERO */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 980, mx: "auto" }}>
            <Typography variant="h4" component="h1" fontWeight={900} gutterBottom>
              About Us
            </Typography>
            <Typography variant="h6" color="text.secondary">
              BhamuIntelli Systems LLP builds modern software products with a strong focus on the
              education domain—learning platforms, assessments, and AI-assisted experiences.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
              <Button variant="contained" component={RouterLink} to="/contact-us">
                Contact Us
              </Button>
              <Button variant="outlined" component={RouterLink} to="/services">
                View Services
              </Button>
              <Button variant="outlined" component={RouterLink} to="/products">
                View Products
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* MISSION / OVERVIEW */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
                  What we do
                </Typography>
                <Typography variant="h6" component="h3" fontWeight={600} gutterBottom>
                  Main Product: -{' '}
                  <a href="https://www.studytunnel.com" target="_blank" rel="noopener noreferrer">
                    https://www.studytunnel.com
                  </a>
                </Typography>


                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  We design and develop web platforms and cloud-native systems that help education
                  teams launch faster and scale confidently.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1}>
                  <Typography variant="body2">• Web apps and portals for students, teachers, and parents</Typography>
                  <Typography variant="body2">• Online exam and assessment systems</Typography>
                  <Typography variant="body2">• AI features: assistants, smart search, insights, and automation</Typography>
                  <Typography variant="body2">• Hosting and delivery on AWS with HTTPS + CDN</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={900} gutterBottom>
                  Company Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1.25}>

                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Registered Name: BhamuIntelli Systems LLP
                    </Typography>

                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>
                      LLP Identification Number (LLPIN)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ACR-5927
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>
                      PAN
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ABFFB*****
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>
                      TAN
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      PNEB2*****
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>
                      Registered Address
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SNO 5/2/1 Daffodils, Gulmohar City, Kharadi, Viman Nagar, Pune 411014,
                      Maharashtra, India
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label="Education Domain" size="small" />
                  <Chip label="Cloud Native" size="small" />
                  <Chip label="Web Platforms" size="small" />
                  <Chip label="AI Integration" size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* VALUES */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: "action.hover" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
              Our values
            </Typography>
            <Typography color="text.secondary">
              How we work with clients and how we build reliable software.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {values.map((v) => (
              <Grid item xs={12} md={3} key={v.title}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center" }}>{v.icon}</Box>
                      <Typography variant="h6" fontWeight={800}>
                        {v.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {v.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Let’s build something valuable in education.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Share your goals and we’ll propose an MVP plan and delivery approach.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button variant="contained" size="large" component={RouterLink} to="/contact-us">
              Contact Us
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/technologies">
              See Technologies
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
};

export default AboutUs;
